import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./NightlatchPaywall.module.scss"

/**
 * NightlatchPaywall (`/nightlatch`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for this
 * run: FEATURE-SPECIFIC.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run is `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it: the most private moment
 * of all is the one you don't have to think about.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "It locks itself. So you never have to." One real Premium safeguard, headlined:
 * AUTO-LOCK. A nightlatch is the lock that latches on its own the instant a door
 * closes — you never turn a key. That is Auto-Lock: the vault's natural, resting
 * state is SEALED. Your photos are only visible while YOU are actively there;
 * the moment you step away — set the phone down, hand it over, or just forget —
 * the nightlatch throws itself and the vault is private again. You cannot leave
 * it open by accident, because staying open is the thing that takes effort.
 *
 * ── Interaction (the hero mechanic) ─────────────────────────────────────────
 * The vault rests SEALED: a frosted brass face, the nightlatch bar thrown across,
 * photos hidden. PRESS AND HOLD the plate to hold it open — the latch retracts,
 * the frost clears, your memories bloom into view, and a "presence" ring fills
 * around your thumb while you stay. LET GO and it seals itself in a beat: the ring
 * collapses, the nightlatch snaps back across, the frost sweeps in, ARMED. Your
 * attention is the ONLY thing holding it open — absence is the lock. (Tap the
 * hint for a timed peek; respects reduced-motion.)
 *
 * ── Divergence (vs. the 95+ prior concepts, incl. the security cluster) ─────
 * Layout      : an upright frosted VAULT WINDOW with a sliding nightlatch bar +
 *               a thumb "presence plate" — not a tabletop device (Flipside), a
 *               folio (Seam), a pattern grid (Keyline/Glyph), rings (Sanctum),
 *               a keypad (Only You) or a CCTV monitor (Caught).
 * Interaction : HOLD-TO-HOLD-OPEN / RELEASE-AUTO-LOCKS — an INVERTED hold. Every
 *               prior hold DOES the lock (Kept seals, Ward converges, Full Circle
 *               traces); here the hold RESISTS the lock and letting go triggers
 *               it. New to the whole set.
 * Metaphor    : the nightlatch — the lock that arms itself the moment you leave.
 * Value frame : freedom from vigilance / risk reversal — you literally cannot
 *               forget to lock it. Not loss aversion, not urgency, not scarcity.
 * Register    : effortless, exhaled relief — "it's already handled."
 * CTA         : "Turn on Auto-Lock" — yearly featured.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Auto-Lock is a REAL Private Photo Vault behaviour (the vault relocks itself
 * when you leave / after inactivity). It is the headline; unlocking Premium turns
 * it on alongside the other real safeguards named (only-you lock · break-in
 * snapshots · encrypted Cloud Vault · app disguise), each shown honestly. No
 * countdown on the offer, no fake scarcity, NO data-loss threat (photos are
 * hidden, never deleted). "Save 79%" is real arithmetic from live product data.
 * Yearly is the featured, default, most prominent price; monthly is small and
 * secondary. The 7-day free trial is the real trial. Renders on built-in debug
 * product data with bundled photos — no native injection, no external egress.
 */

// The vault behind the frosted face — bundled, self-contained memory photos
// reused from the repo. Read as "your whole album" at a glance.
const VAULT = [
  "/keyline/k1.jpg",
  "/keyline/k2.jpg",
  "/keyline/k3.jpg",
  "/keyline/k4.jpg",
  "/keyline/k5.jpg",
  "/keyline/k6.jpg",
]

// The other real safeguards switched on with Premium. Auto-Lock is the hero
// above; these are the supporting cast, kept deliberately secondary.
const ALSO = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Pattern, Face ID or a code only you know",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "A silent photo of anyone who tries a wrong code",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "A private, encrypted backup of every memory",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "Hide the whole vault behind an ordinary app",
  },
]

const NightlatchPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // Blend the safe-area insets into the scene so it runs edge to edge.
  useEffect(() => {
    actions.setHeaderInsetColor("#0a0c14")
    actions.setFooterInsetColor("#070810")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── open state ────────────────────────────────────────────────────────────
  // o: 0 = sealed / auto-locked (resting state), 1 = held fully open. Your
  // presence eases it toward 1; absence eases it back to 0 (the nightlatch
  // throwing itself). Locked once nearly closed.
  const [o, setO] = useState(0)
  const oRef = useRef(0)
  const targetRef = useRef(0)
  const raf = useRef<number | null>(null)
  const userActed = useRef(false)
  const peekTimer = useRef<number | null>(null)

  const locked = o < 0.12

  const stopRaf = useCallback(() => {
    if (raf.current != null) cancelAnimationFrame(raf.current)
    raf.current = null
  }, [])

  // A single persistent spring that chases targetRef. Opening (toward 1) eases
  // in gently; releasing (toward 0) snaps a touch harder — the latch is decisive.
  const tick = useCallback(() => {
    const target = targetRef.current
    const cur = oRef.current
    const k = target > cur ? 0.16 : 0.24
    let next = cur + (target - cur) * k
    if (Math.abs(target - next) < 0.0015) next = target
    oRef.current = next
    setO(next)
    if (next !== target) raf.current = requestAnimationFrame(tick)
    else raf.current = null
  }, [])

  const setTarget = useCallback(
    (v: number) => {
      targetRef.current = v
      if (reduceMotion) {
        stopRaf()
        oRef.current = v
        setO(v)
        return
      }
      if (raf.current == null) raf.current = requestAnimationFrame(tick)
    },
    [reduceMotion, stopRaf, tick],
  )

  const clearPeek = useCallback(() => {
    if (peekTimer.current != null) window.clearTimeout(peekTimer.current)
    peekTimer.current = null
  }, [])

  // Auto-demo: hold it open for a beat so the payoff reads, then let go so the
  // nightlatch visibly throws itself and teaches the whole idea in one motion.
  useEffect(() => {
    if (window.location.search.includes("nodemo")) return
    const timers: number[] = []
    timers.push(
      window.setTimeout(() => {
        if (!userActed.current) setTarget(1)
      }, 750),
    )
    timers.push(
      window.setTimeout(() => {
        if (!userActed.current) setTarget(0)
      }, 2450),
    )
    return () => {
      timers.forEach((x) => window.clearTimeout(x))
      stopRaf()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // ── hold-to-hold-open ──────────────────────────────────────────────────────
  const beginHold = useCallback(
    (e: React.PointerEvent) => {
      userActed.current = true
      clearPeek()
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
      setTarget(1)
    },
    [clearPeek, setTarget],
  )

  const endHold = useCallback(() => {
    // Absence is the lock: the instant you let go, it seals itself.
    setTarget(0)
  }, [setTarget])

  // Tap / keyboard fallback — a timed "peek" that auto-locks itself, so the
  // point still lands without a press-and-hold.
  const peek = useCallback(() => {
    userActed.current = true
    clearPeek()
    setTarget(1)
    peekTimer.current = window.setTimeout(() => setTarget(0), 1500)
  }, [clearPeek, setTarget])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  // Presence-ring geometry (a stroke that fills with how far open it is).
  const RING = 2 * Math.PI * 30

  return (
    <div
      className={`${styles.root} ${locked ? styles.isLocked : ""}`}
      style={{ ["--o" as any]: o }}
    >
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12.2l2.2 2.2L15 10.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Premium safeguard · Auto-Lock
          </span>
          <h1 className={styles.title}>
            {locked ? (
              <>
                Let go.
                <br />
                <span className={styles.accent}>It locks itself.</span>
              </>
            ) : (
              <>
                Open — but only
                <br />
                <span className={styles.accent}>while you're here.</span>
              </>
            )}
          </h1>
          <p className={styles.sub}>
            Auto-Lock seals your vault the instant you step away. No code to
            remember to tap, no window left open by mistake — you can't forget a
            lock that arms itself.
          </p>
        </section>

        {/* ── the nightlatch (hero) ──────────────────────────────────────── */}
        <section className={styles.stage}>
          <div
            className={styles.window}
            aria-label="Hold to hold the vault open; let go and it locks itself"
          >
            {/* the memories behind the frosted face */}
            <div className={styles.vault} aria-hidden="true">
              <div className={styles.grid}>
                {VAULT.map((src, i) => (
                  <span
                    key={i}
                    className={styles.tile}
                    style={{
                      backgroundImage: `url(${src})`,
                      ["--i" as any]: i,
                    }}
                  />
                ))}
              </div>
              {/* privacy frost that sweeps in as the latch throws */}
              <span className={styles.frost} />
              {/* a faint "your photos" watermark while sealed */}
              <span className={styles.sealedGlyph} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="30" height="30">
                  <rect
                    x="3.5"
                    y="5"
                    width="17"
                    height="14"
                    rx="2.4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <circle
                    cx="8.5"
                    cy="10"
                    r="1.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                  />
                  <path
                    d="M4.5 17l4.5-4.2 3.2 2.8 3.4-3.4 4.4 4.2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Your photos</span>
              </span>
            </div>

            {/* the sliding nightlatch bar — thrown across when sealed */}
            <div className={styles.latch} aria-hidden="true">
              <span className={styles.latchBar}>
                <span className={styles.latchKnob} />
                <span className={styles.latchGroove} />
              </span>
              <span className={styles.strike} />
            </div>

            {/* the status plate */}
            <div
              className={`${styles.plate} ${
                locked ? styles.plateOn : styles.plateOff
              }`}
              aria-hidden="true"
            >
              <span className={styles.plateDot} />
              {locked ? "SEALED · AUTO-LOCK ARMED" : "OPEN · YOU'RE HERE"}
            </div>
          </div>

          {/* the presence plate — press & hold to keep it open */}
          <button
            type="button"
            className={styles.hold}
            onPointerDown={beginHold}
            onPointerUp={endHold}
            onPointerCancel={endHold}
            onPointerLeave={endHold}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                peek()
              }
            }}
            aria-label={
              locked ? "Hold to hold the vault open" : "Let go to seal"
            }
          >
            <span className={styles.holdRing} aria-hidden="true">
              <svg viewBox="0 0 68 68" width="68" height="68">
                <circle
                  cx="34"
                  cy="34"
                  r="30"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  className={styles.ringTrack}
                />
                <circle
                  cx="34"
                  cy="34"
                  r="30"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  className={styles.ringFill}
                  style={{
                    strokeDasharray: RING,
                    strokeDashoffset: RING * (1 - o),
                    transform: "rotate(-90deg)",
                    transformOrigin: "34px 34px",
                  }}
                />
              </svg>
              <span className={styles.holdThumb}>
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    d="M12 3v9M12 12c0 2.2 1.8 4 4 4M12 12c0 2.2-1.8 4-4 4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeDasharray="3 3"
                    opacity="0.5"
                  />
                </svg>
              </span>
            </span>
            <span className={styles.holdLabel}>
              {locked ? "Hold to look inside" : "Let go to seal"}
            </span>
          </button>
        </section>

        {/* ── the everyday proof (honest framing of the hero feature) ─────── */}
        <p className={styles.proof}>
          <span className={styles.proofMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Set it down, hand it over, or simply forget — the vault doesn't wait
          for you to lock it. It's already private the moment you're not there.
        </p>

        {/* ── comes switched on with Premium (supporting, secondary) ─────── */}
        <ul className={styles.also}>
          <li className={styles.alsoHead}>
            <span>Switched on with Premium</span>
            <span className={styles.alsoCount}>
              {ALSO.length + 1} safeguards
            </span>
          </li>
          <li className={`${styles.alsoRow} ${styles.alsoHero}`}>
            <span className={styles.alsoIcon} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="17" height="17">
                <rect
                  x="4.5"
                  y="10.5"
                  width="15"
                  height="9.5"
                  rx="2.2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <path
                  d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="15" r="1.4" fill="currentColor" />
              </svg>
            </span>
            <span className={styles.alsoText}>
              <span className={styles.alsoName}>Auto-Lock</span>
              <span className={styles.alsoLine}>
                Seals the vault by itself the moment you leave
              </span>
            </span>
            <span className={styles.alsoBadge}>This screen</span>
          </li>
          {ALSO.map((g) => (
            <li key={g.key} className={styles.alsoRow}>
              <span className={styles.alsoIcon} aria-hidden="true">
                <g.Icon width={17} height={17} />
              </span>
              <span className={styles.alsoText}>
                <span className={styles.alsoName}>{g.name}</span>
                <span className={styles.alsoLine}>{g.line}</span>
              </span>
              <span className={styles.alsoTick} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </li>
          ))}
        </ul>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Premium · 1 year
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
            <span className={styles.yearlyFoot}>
              Auto-Lock and every safeguard, on for a full year
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or monthly</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Turn on Auto-Lock — ${trialDays}-day free trial`
              : "Turn on Auto-Lock"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default NightlatchPaywall
